import { useEffect, useState } from "react";
import { Loader2, Crown, Medal, Users, Gift } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { REDEEM_URL } from "@/config/social";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const podiumStyle = [
  { ring: "border-yellow-400 bg-yellow-50", badge: "bg-yellow-400 text-zinc-950", icon: "text-yellow-500" },
  { ring: "border-slate-300 bg-slate-50", badge: "bg-slate-300 text-zinc-950", icon: "text-slate-400" },
  { ring: "border-amber-600 bg-amber-50", badge: "bg-amber-600 text-white", icon: "text-amber-600" },
];

function PredictionsBoard({ username }) {
  const [periods, setPeriods] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/periods/all").then((r) => {
      const active = (r.data || []).filter((p) => p.status === "active");
      setPeriods(active);
      setSelectedId(active.length ? active[0].id : "");
    });
  }, []);

  useEffect(() => {
    if (selectedId === null) return;
    setData(null);
    const url = selectedId ? `/leaderboard?period_id=${selectedId}` : "/leaderboard";
    api.get(url).then((r) => setData(r.data));
  }, [selectedId]);

  if (!periods) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>;

  return (
    <div className="space-y-5">
      {periods.length > 1 && (
        <div data-testid="classifiche-selector" className="flex gap-2 overflow-x-auto pb-1">
          {periods.map((p) => (
            <button
              key={p.id}
              data-testid={`classifica-chip-${p.id}`}
              onClick={() => setSelectedId(p.id)}
              className={`shrink-0 rounded-full border-2 px-4 py-2 text-sm font-black uppercase tracking-tight transition-colors ${
                selectedId === p.id ? "border-zinc-950 bg-zinc-950 text-green-400" : "border-slate-200 bg-white text-zinc-600"
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}
      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>
      ) : (
        <LeaderboardTable data={data} username={username} />
      )}
    </div>
  );
}

function LeaderboardTable({ data, username }) {
  const rows = data.rows || [];
  return (
    <div className="space-y-6">
      {data.period && (
        <div data-testid="classifica-prize-card" className="overflow-hidden rounded-3xl border-2 border-zinc-950 bg-zinc-950 text-white">
          {data.period.prize_image && <img src={fileUrl(data.period.prize_image)} alt="" className="h-40 w-full object-cover" />}
          <div className="p-5">
            <p className="font-display text-xl font-black uppercase">{data.period.name}</p>
            {data.period.prize ? (
              <>
                <p className="mt-1 flex items-center gap-2 text-sm text-zinc-300"><Gift className="h-4 w-4 text-green-400" /> In palio: <span className="font-bold text-white">{data.period.prize}</span></p>
                <a data-testid="riscatta-premio-btn" href={REDEEM_URL} target="_blank" rel="noopener noreferrer"
                  className="mt-4 flex w-full items-center justify-center rounded-full bg-green-500 py-3 text-sm font-black uppercase tracking-wide text-zinc-950 transition-colors hover:bg-green-400">
                  Riscatta il tuo premio
                </a>
              </>
            ) : <p className="mt-1 text-sm text-zinc-400">Premio non ancora annunciato.</p>}
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-slate-300 bg-white p-10 text-center text-zinc-500">
          Ancora nessun punto assegnato. Sii il primo a scalare la classifica!
        </div>
      ) : (
        <div data-testid="leaderboard-list" className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <div className="grid grid-cols-[2rem_1fr_2.6rem_2.4rem_2.4rem_3rem] items-center gap-1 bg-zinc-950 px-3 py-2.5 text-[9px] font-black uppercase tracking-wide text-green-400">
            <span>#</span>
            <span>Giocatore</span>
            <span className="text-center">Gioc.</span>
            <span className="text-center">✓</span>
            <span className="text-center">✗</span>
            <span className="text-right">Punti</span>
          </div>
          {rows.map((r) => (
            <div key={r.user_id} data-testid={`leaderboard-user-row-${r.rank}`}
              className={`grid grid-cols-[2rem_1fr_2.6rem_2.4rem_2.4rem_3rem] items-center gap-1 border-b border-slate-100 px-3 py-2.5 last:border-0 ${r.username === username ? "bg-green-50" : ""}`}>
              <span className={`font-mono-t text-sm font-black ${r.rank === 1 ? "text-yellow-500" : r.rank === 2 ? "text-slate-400" : r.rank === 3 ? "text-amber-600" : "text-zinc-400"}`}>{r.rank}</span>
              <span className="truncate font-semibold text-zinc-900">{r.username}</span>
              <span className="text-center font-mono-t text-sm font-bold text-zinc-600">{r.answered}</span>
              <span className="text-center font-mono-t text-sm font-bold text-green-600">{r.correct}</span>
              <span className="text-center font-mono-t text-sm font-bold text-red-500">{r.wrong}</span>
              <span className="text-right font-mono-t text-sm font-black text-zinc-950">{r.points}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferralBoard({ username }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/referral/leaderboard").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>;
  const rows = data.rows || [];
  return (
    <div className="space-y-4">
      {data.prize && (
        <div className="flex items-center gap-2 rounded-2xl bg-zinc-950 p-4 text-white" data-testid="referral-prize-banner">
          <Gift className="h-5 w-5 text-green-400" />
          <span className="text-sm">Premio "Porta un amico": <strong>{data.prize}</strong></span>
        </div>
      )}
      {rows.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-slate-300 bg-white p-10 text-center text-zinc-500">
          Nessun invito ancora. Condividi il tuo link dal Profilo e inizia a guadagnare punti!
        </div>
      ) : (
        <div data-testid="referral-leaderboard-table" className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
          {rows.map((r) => (
            <div key={r.username} data-testid={`referral-row-${r.rank}`}
              className={`flex items-center gap-4 border-b border-slate-100 px-4 py-3 last:border-0 ${r.username === username ? "bg-green-50" : ""}`}>
              <span className="w-8 font-mono-t text-sm font-black text-zinc-400">{r.rank}</span>
              <span className="flex-1 truncate font-semibold text-zinc-900">{r.username}</span>
              <span className="flex items-center gap-1 text-xs font-semibold text-zinc-400"><Users className="h-3 w-3" />{r.invited_count}</span>
              <span className="w-16 text-right font-mono-t font-black text-green-600">{r.points} pt</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight text-zinc-950">Classifiche</h1>
      </header>
      <Tabs defaultValue="predictions">
        <TabsList className="mb-4 grid w-full grid-cols-2 gap-2 bg-transparent p-0">
          <TabsTrigger data-testid="leaderboard-tab-predictions" value="predictions" className="rounded-full border border-slate-200 bg-white py-2 font-bold data-[state=active]:bg-zinc-950 data-[state=active]:text-green-400">Pronostici</TabsTrigger>
          <TabsTrigger data-testid="leaderboard-tab-referral" value="referral" className="rounded-full border border-slate-200 bg-white py-2 font-bold data-[state=active]:bg-zinc-950 data-[state=active]:text-green-400">Porta un amico</TabsTrigger>
        </TabsList>
        <TabsContent value="predictions"><PredictionsBoard username={user.username} /></TabsContent>
        <TabsContent value="referral"><ReferralBoard username={user.username} /></TabsContent>
      </Tabs>
    </div>
  );
}
